import _ from "lodash";
import { useCallback, useRef, useState } from "react";
import moment from "moment";
import { initialAddFleet, initialVesselState } from "../../constants";
import * as actions from "../../containers/User/Calculator/actions";
import * as dashboardActions from "../../containers/User/Dashboard/actions";
import { toastError, toastSuccess } from "../../utils";
import CustomAddModal from "../Common/CustomAddModal";
import AddNewVessel, { VesselAIModal } from "../Dashboard/AddNewVessel";

// Fields that belong to the fleet row, not the Vessel record (same split as Fleet/myVessel.js).
const fleetFieldKeys = [
  "port", "lat", "lng",
  "bnkDelhs", "bnkDells", "bnkDelmgo",
  "bnkPriceDeliveryhs", "bnkPriceDeliveryls", "bnkPriceDeliverymgo",
  "bnkPriceRedeliveryhs", "bnkPriceRedeliveryls", "bnkPriceRedeliverymgo",
  "bnkPriceSettlemenths", "bnkPriceSettlementls", "bnkPriceSettlementmgo",
  "bnkQtyRedeliveryhs", "bnkQtyRedeliveryls", "bnkQtyRedeliverymgo",
  "bnkRedeliveryQtyTolerancehs", "bnkRedeliveryQtyTolerancels", "bnkRedeliveryQtyTolerancemgo",
  "bunkerPriceOverridden", "dateOpen", "gbb", "hirePerDay", "hireGbb", "remark", "constants", "extraExpense", "splitHire",
];

/**
 * Create-vessel flow for pages outside the Vessel Manager (e.g. Matrix).
 * mode: "ai" opens the AI prompt first, "manual" opens the form; null closes both.
 * onCreated runs after the vessel is saved so the caller can refresh its list.
 */
function CreateVesselModals({ mode, onClose, onCreated = () => { }, setLoader = () => { } }) {
  const [newVessel, setNewVessel] = useState({ ...initialVesselState });
  const [formOpen, setFormOpen] = useState(false);
  const [portList, setPortList] = useState([]);
  const [portLoading, setPortLoading] = useState(false);
  // The AI modal closes itself after it fills the form; that must not close the form too.
  const aiFilledRef = useRef(false);

  const aiOpen = mode === "ai" && !formOpen;
  const manualOpen = mode === "manual" || formOpen;

  const close = () => {
    aiFilledRef.current = false;
    setFormOpen(false);
    setNewVessel({ ...initialVesselState });
    onClose();
  };

  const searchPorts = (value) => {
    if (!value) return setPortLoading(false);
    setPortLoading(true);
    actions.portsByChar(value)
      .then((res) => {
        setPortLoading(false);
        setPortList(
          Array.isArray(res.data.list)
            ? res.data.list.map((item) => ({
              ...item,
              portLabel: `${item.name}${item.country ? ` (${item.country})` : ""}`,
            }))
            : []
        );
      })
      .catch(() => setPortLoading(false));
  };
  const delayPortSearch = useCallback(_.debounce(searchPorts, 500), []);

  const onChangeHandler = (e) => {
    let name = "";
    let value = "";
    let type = "text";
    let checked = false;
    if (e._isAMomentObject) {
      name = "dateOpen";
      value = e.toISOString();
    } else if (e.target) {
      ({ name = "", value = "", type = "checkbox", checked } = e.target);
    }
    setNewVessel((prev) => {
      const next = { ...prev };
      if (type === "checkbox") next[name] = checked;
      else {
        if (value && value.toString().split("")[0] === 0) return prev;
        next[name] = value;
      }
      return next;
    });
  };

  const parseSplitHire = (vessel) => (vessel?.splitHire && JSON.parse(vessel.splitHire)) || [];

  const addNewSplitHire = () => {
    const splitHire = parseSplitHire(newVessel);
    if (splitHire.find((item) => item.NoDays === 0)) return;
    splitHire.push({ NoDays: 0, applied: 0, disable: false, hireRate: 0 });
    setNewVessel({ ...newVessel, splitHire: JSON.stringify(splitHire) });
  };

  const onChangeSplitHire = (index, e, isDelete = false) => {
    const splitHire = parseSplitHire(newVessel);
    if (isDelete) splitHire.splice(index, 1);
    else {
      const { name = "", value = "" } = e.target;
      splitHire[index][name] = Number(value);
    }
    setNewVessel({ ...newVessel, splitHire: JSON.stringify(splitHire) });
  };

  const onChangeBunkerPriceOverridden = (e) => {
    const { name = "", checked = "" } = e.target || {};
    setNewVessel({ ...newVessel, [name]: checked });
  };

  const onChangePort = (name, value) => {
    setNewVessel((prev) => ({
      ...prev,
      port: value.name,
      selectedPort: value,
      lat: value.lat || "",
      lng: value.lng || "",
    }));
  };

  const onSubmit = async () => {
    if (!newVessel.vesselName) return toastError("Vessel Name is required");
    setLoader(true);

    const fleetFields = Object.fromEntries(
      fleetFieldKeys.map((key) => [key, newVessel[key]]).filter(([, v]) => v !== undefined)
    );
    const vesselRecord = {
      ...newVessel,
      vesselExtraDetails: { ...newVessel.vesselExtraDetails, vesselTextDescription: newVessel.vesselTextDescription },
    };

    try {
      // Same two steps as Vessel Manager "Add to My Vessels": create the Vessel record, then
      // add a fleet row that points at it. (POST /fleet rejects a name that already has a Vessel.)
      const res = await dashboardActions.createVessel(vesselRecord);
      const { selectedPort: _port, selectedVessel: _vessel, ...row } = {
        ...initialAddFleet,
        ...fleetFields,
        vesselName: newVessel.vesselName,
        selectedVessel: { ...initialAddFleet.selectedVessel, ...res?.data },
        selectedPort: { ...initialAddFleet.selectedPort, ...newVessel.selectedPort },
      };
      const selectedVessel = { ...initialAddFleet.selectedVessel, ...res?.data };
      await actions.addMultiFleet([
        {
          ...row,
          dateOpen: moment(row.dateOpen).toISOString(),
          vesselId: selectedVessel._id,
          vessel: JSON.stringify(selectedVessel),
        },
      ]);
      toastSuccess("Vessel Created Successfully");
      close();
      onCreated();
    } catch (err) {
      toastError(err?.data?.message || "Could not create vessel");
    }
    setLoader(false);
  };

  return (
    <>
      <VesselAIModal
        aiOpen={aiOpen}
        setAiOpen={(open) => { if (!open && !aiFilledRef.current) close(); }}
        onAiFill={(patch) => {
          aiFilledRef.current = true;
          setNewVessel((prev) => ({ ...prev, ...patch }));
          setFormOpen(true);
        }}
      />
      <CustomAddModal
        open={manualOpen}
        saveLabel="Add"
        title="Create New Vessel"
        maxWidth="md"
        handleClose={close}
        onClose={close}
        onSubmitConfirm={onSubmit}
      >
        <AddNewVessel
          vessel={newVessel}
          onChangeHandler={onChangeHandler}
          addNewSplitHire={addNewSplitHire}
          onChangeBunkerPriceOverridden={onChangeBunkerPriceOverridden}
          onChangeSplitHire={onChangeSplitHire}
          delayPortSearch={delayPortSearch}
          portLoading={portLoading}
          onChangePort={onChangePort}
          savePortList={portList}
        />
      </CustomAddModal>
    </>
  );
}

export default CreateVesselModals;
